import React from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SubHeader from '../../components/Navbars/SubHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const getThemeTokens = (isLightTheme) => ({
  cardBg: isLightTheme ? '#ffffff' : '#27293d',
  cardBorder: isLightTheme ? '#eee' : '#3c3f58',
  headerBg: isLightTheme ? '#fff' : '#27293d',
  headerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  titleColor: isLightTheme ? '#333' : '#f1f5f9',
  rowColor: isLightTheme ? '#333' : 'rgba(255, 255, 255, 0.75)',
  valueColor: isLightTheme ? '#333' : '#ffffff',
  dividerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  loadingOverlay: isLightTheme ? 'rgba(255,255,255,0.3)' : 'rgba(30, 30, 47, 0.55)',
  labelPluginFill: isLightTheme ? '#333' : '#cbd5e1',
  labelLineStroke: isLightTheme ? 'rgba(180, 180, 180, 0.6)' : 'rgba(148, 163, 184, 0.55)',
  emptyStateColor: isLightTheme ? '#888' : '#94a3b8',
  tooltipBg: isLightTheme ? '#1f2937' : '#0f172a',
  tooltipTitle: isLightTheme ? '#f8fafc' : '#f1f5f9',
  tooltipBody: isLightTheme ? '#e2e8f0' : '#cbd5e1',
  tooltipBorder: isLightTheme ? '#334155' : '#1e293b',
  legendColor: isLightTheme ? '#666' : '#94a3b8',
  shadow: isLightTheme ? '0 2px 4px rgba(0,0,0,0.05)' : '0 2px 8px rgba(0,0,0,0.25)',
});

const CHART_PALETTES = {
  sales: {
    light: ['#d1b975', '#6aa0c3', '#a8cbac', '#e7d9d0'],
    dark: ['#d1b975', '#6aa0c3', '#a8cbac', '#e7d9d0'],
  },
  loading: {
    light: ['#f0f0f0', '#f5f5f5', '#e9e9e9', '#efefef'],
    dark: ['#3c3f58', '#45486a', '#3a3d56', '#404362'],
  },
};

const SalesDashboard = ({
  statistics,
  activeColor,
  isLoading,
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const theme = React.useMemo(() => getThemeTokens(isLightTheme), [isLightTheme]);
  const paletteKey = isLightTheme ? 'light' : 'dark';
  const chartColors = CHART_PALETTES.sales[paletteKey];
  const loadingChartColors = CHART_PALETTES.loading[paletteKey];

  const formatValue = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  };

  const getPeriodData = (periodId) => {
    const period = statistics?.sales?.[periodId] || {
      data: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
      counts: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
      total_data: 0,
      total_counts: 0
    };

    const rowData = [
      { id: 'cash', label: 'Cash', rev: period.data?.cash || 0, count: period.counts?.cash || 0 },
      { id: 'in_house_finance', label: 'In-House', rev: period.data?.in_house_finance || 0, count: period.counts?.in_house_finance || 0 },
      { id: 'local_lender_finance', label: 'Outside', rev: period.data?.local_lender_finance || 0, count: period.counts?.local_lender_finance || 0 },
      { id: 'wholesale', label: 'Wholesale', rev: period.data?.wholesale || 0, count: period.counts?.wholesale || 0 },
    ];

    return {
      rows: rowData,
      labels: rowData.map(x => x.label),
      revs: rowData.map(x => x.rev),
      counts: rowData.map(x => x.count),
      totalRev: period.total_data || 0,
      totalCount: period.total_counts || 0,
    };
  };

  const today = getPeriodData('today');
  const thisWeek = getPeriodData('week');
  const thisMonth = getPeriodData('this_month');
  const thisQuarter = getPeriodData('this_quarter');
  const thisYear = getPeriodData('this_year');

  const renderSalesCard = (title, data) => {
    const hasData = data.totalRev > 0;
    const displayRevs = (isLoading && !hasData) ? [1, 1, 1, 1] : data.revs;
    const displayLabels = (isLoading && !hasData) ? ["Cash", "In-House", "Outside", "Wholesale"] : data.labels;

    return (
      <Col xl="3" lg="4" md="6" className="mb-2">
        <Card className="avg-on-lot-card">
          <div className="avg-on-lot-header">
            <h4 className="avg-on-lot-title">{title}</h4>
          </div>
          <CardBody className='pt-0'>
            <div className="card-inner-relative" style={{ position: 'relative' }}>
              {isLoading && (
                <div className="loading-ghost-overlay">
                  <div className="pulse-loading">Loading...</div>
                </div>
              )}
              <div className="old-style-list" style={{ marginBottom: '15px' }}>
                <div
                  className="old-style-row old-style-row--divider"
                  style={{ paddingBottom: '4px', borderBottom: `1px solid ${theme.dividerBorder}`, marginBottom: '8px' }}
                >
                  <span className="old-style-label" style={{ flex: 2, fontSize: '13px' }}>Totals Sales</span>
                  <span className="old-style-value" style={{ flex: 1, textAlign: 'center', fontSize: '13px' }}>{!hasData && isLoading ? "-" : data.totalCount}</span>
                  <span className="old-style-value" style={{ flex: 2, textAlign: 'right', fontSize: '13px' }}>{!hasData && isLoading ? "-" : formatValue(data.totalRev)}</span>
                </div>
                {data.rows.map(row => (
                  <div className="old-style-row" key={row.id}>
                    <span className="old-style-label" style={{ flex: 2, fontSize: '13px' }}>{row.label}</span>
                    <span className="old-style-value" style={{ flex: 1, textAlign: 'center', fontSize: '13px' }}>{!hasData && isLoading ? "-" : row.count}</span>
                    <span className="old-style-value" style={{ flex: 2, textAlign: 'right', fontSize: '13px' }}>{!hasData && isLoading ? "-" : formatValue(row.rev)}</span>
                  </div>
                ))}
              </div>
              <div
                className="chart-container-border"
                style={{
                  height: '320px',
                  border: `1px solid ${theme.cardBorder}`,
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {(hasData || isLoading) ? (
                  <Doughnut
                    key={isLoading ? 'loading' : 'ready'}
                    data={{
                      labels: displayLabels,
                      datasets: [
                        {
                          data: displayRevs,
                          backgroundColor: isLoading && !hasData ? loadingChartColors : chartColors,
                          borderColor: theme.cardBg,
                          borderWidth: 2,
                        }
                      ]
                    }}
                    plugins={[
                      {
                        id: 'doughnutLabelsLine',
                        afterDatasetsDraw(chart) {
                          const { ctx, data: chartData } = chart;
                          ctx.save();
                          const meta = chart.getDatasetMeta(0);
                          meta.data.forEach((element, index) => {
                            const value = chartData.datasets[0].data[index];
                            if (!value || value === 0) return;
                            const { x, y, startAngle, endAngle, outerRadius } = element;
                            const middleAngle = startAngle + (endAngle - startAngle) / 2;
                            const posX = x + Math.cos(middleAngle) * outerRadius;
                            const posY = y + Math.sin(middleAngle) * outerRadius;
                            const lineLen = 15;
                            const endX = x + Math.cos(middleAngle) * (outerRadius + lineLen);
                            const endY = y + Math.sin(middleAngle) * (outerRadius + lineLen);
                            ctx.beginPath();
                            ctx.moveTo(posX, posY);
                            ctx.lineTo(endX, endY);
                            ctx.strokeStyle = theme.labelLineStroke;
                            ctx.lineWidth = 1;
                            ctx.stroke();
                            const horizontalLen = 10;
                            const finalX = endX + (Math.cos(middleAngle) > 0 ? horizontalLen : -horizontalLen);
                            ctx.beginPath();
                            ctx.moveTo(endX, endY);
                            ctx.lineTo(finalX, endY);
                            ctx.stroke();
                            const textValue = (isLoading && !hasData) ? "-" : formatValue(value);
                            ctx.font = "bold 11px Arial";
                            ctx.fillStyle = theme.labelPluginFill;
                            const isRightSide = Math.cos(middleAngle) > 0;
                            ctx.textAlign = isRightSide ? "left" : "right";
                            ctx.textBaseline = "middle";
                            ctx.fillText(textValue, finalX + (isRightSide ? 8 : -8), endY);
                          });
                          ctx.restore();
                        }
                      }
                    ]}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '70%',
                      layout: {
                        padding: {
                          left: 65,
                          right: 65,
                          top: 5,
                          bottom: 25
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeOutQuart',
                        delay: (context) => context.dataIndex * 150
                      },
                      plugins: {
                        legend: {
                          display: !isLoading,
                          position: 'top',
                          labels: {
                            padding: 5,
                            boxWidth: 10,
                            font: { size: 11 },
                            usePointStyle: true,
                            pointStyle: 'rect',
                            color: theme.legendColor,
                          }
                        },
                        tooltip: {
                          enabled: !isLoading,
                          backgroundColor: theme.tooltipBg,
                          titleColor: theme.tooltipTitle,
                          bodyColor: theme.tooltipBody,
                          borderColor: theme.tooltipBorder,
                          borderWidth: 1,
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const val = context.raw || 0;
                              const total = data.totalRev || 1;
                              const perc = ((val / total) * 100).toFixed(1);
                              return `${label}: ${formatValue(val)} (${perc}%)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '12px', color: theme.emptyStateColor, textAlign: 'center' }}>No sales data available</div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <div className="sales-dashboard-container">
      <SubHeader
        activeColor={activeColor}
        back={"/dashboard"}
        title={"Sales Dashboard"}
        theme2ScrollGapFill={shellThemeVariant === "2"}
        isLightTheme={isLightTheme}
      />

      <style>{`
        .sales-dashboard-container .avg-on-lot-card {
          background: ${theme.cardBg};
          border: 1px solid ${theme.cardBorder};
          margin-bottom: 20px;
          color: ${theme.valueColor};
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: ${theme.shadow};
        }
        .sales-dashboard-container .avg-on-lot-header {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px;
          border-bottom: 1px solid ${theme.headerBorder};
          background: ${theme.headerBg};
        }
        .sales-dashboard-container .avg-on-lot-title {
          font-weight: 600;
          font-size: 13px;
          color: ${theme.titleColor};
          margin: 0;
        }
        .sales-dashboard-container .old-style-list {
          padding: 10px 5px;
        }
        .sales-dashboard-container .old-style-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: 13px;
          color: ${theme.rowColor};
        }
        .sales-dashboard-container .old-style-label {
          font-weight: 500;
        }
        .sales-dashboard-container .old-style-value {
          font-weight: 600;
          color: ${theme.valueColor};
        }
        .sales-dashboard-container .loading-ghost-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${theme.loadingOverlay};
          backdrop-filter: blur(1px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          pointer-events: none;
        }
        .sales-dashboard-container .pulse-loading {
          font-weight: 600;
          font-size: 13px;
          color: #1d8cf8;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          animation: sales-dashboard-pulse 1.5s infinite ease-in-out;
        }
        @keyframes sales-dashboard-pulse {
          0% { opacity: 0.4; transform: scale(0.98); }
          50% { opacity: 1; transform: scale(1); }
          100% { opacity: 0.4; transform: scale(0.98); }
        }
      `}</style>

      <Row className="d-flex align-items-stretch">
        {renderSalesCard("Sales Today", today)}
        {renderSalesCard("Sales This Week", thisWeek)}
        {renderSalesCard("Sales This Month", thisMonth)}
        {renderSalesCard("Sales This Quarter", thisQuarter)}
        {renderSalesCard("Sales This Year", thisYear)}
      </Row>
    </div>
  );
};

export default SalesDashboard;
